<template>
    <!-- *********************************************
    *                   Account Block                *
    ********************************************** -->
    <divider-generic
    dividerSize="medium"
    icon="account-balance"
    :applyBorder="false"
    :title="t('accounts.extracts.step1.accountTitle')"/>
    <!-- Account Dropdown -->
    <dropdown-account
    class="account-dropdown"
    id="accountId"
    :params="accountDropdownParams"
    :options="getAccountsBalances"
    :selectedOption="extractData.accountId"
    @clicked="[emit('extraDataChange', { field: 'accountId', inputValue: $event }), clearFieldError('accountIdParams')]"/>
    <!-- *********************************************
    *                  Date Info Block               *
    ********************************************** -->
    <divider-generic
    icon="calendar"
    dividerSize="medium"
    :title="t('accounts.extracts.step1.dateTitle')"/>
    <!-- Info -->
    <infobox-generic :label="t('accounts.extracts.step1.textBlockData')"/>
    <!-- Double Calendar -->
    <input-double-calendar
    class="inputs-size"
    :fromDateParams="{...fromDateParams, inputValue: extractData.beginDate}"
    :toDateParams="{...toDateParams, inputValue: extractData.endDate}"
    @inputFromDate="[emit('extraDataChange', { field: 'beginDate', inputValue: $event }), clearFieldError('beginDateParams')]"
    @inputToDate="[emit('extraDataChange', { field: 'endDate', inputValue: $event }), clearFieldError('endDateParams')]"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import InputDoubleCalendar from '@/components/generic/inputs/DoubleCalendar'

/* Vue & Assets */
import { ref, watch } from 'vue'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['extraDataChange', 'scrollTo', 'resetShowMandatoryErrorsProp', 'formErrorsCleared'])

/* props received
@extractData {Object} object with info for our current form
@mandatoryList {Array} Array of mandatory keys corresponding with our form
@showMandatoryErrors {Boolean} Prop to trigger display of mandatory errors
@clearFormErrors {Boolean} Prop to remove of form errors
*/
const props = defineProps({
    extractData: Object,
	mandatoryList: Array,
	showMandatoryErrors: Boolean,
	clearFormErrors: Boolean
})

/* mapGetters */
const { getAccountsBalances } = mapGetters(moduleMapper.ACCOUNTS_EXTRACTS)

const accountDropdownParams = ref({
	labelText: t('accounts.extracts.step1.dropdownLabel'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

const fromDateParams = ref({
    domIdentifier: 'beginDate',
	labelText: t('accounts.extracts.step1.startDate'),
    placeholder: t('accounts.extracts.step1.startDate'),
	mandatory: true,
    disabledDates: { from: new Date() },
	errorState: false,
    errorMessage: null
})

const toDateParams = ref({
    domIdentifier: 'endDate',
	labelText: t('accounts.extracts.step1.endDate'),
    placeholder: t('accounts.extracts.step1.endDate'),
	mandatory: true,
    disabledDates: { from: new Date() },
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	accountIdParams: accountDropdownParams.value,
    beginDateParams: fromDateParams.value,
    endDateParams: toDateParams.value
})

watch(() => props.showMandatoryErrors, (newValue, oldValue) => {
	if (newValue) showMandatoryErrorsHandler()
	emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */
	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.extractData, props.mandatoryList)

	emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

    mandatoryFieldsEmptyList.forEach(fieldName => {
        allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        allFormFields.value[`${fieldName}Params`].errorState = true
    })
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, (newValue, oldValue) => {
	clearFormErrors()
	emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
	for (var fieldName in allFormFields.value) {
		clearFieldError(fieldName)
	}
}
</script>

<style lang="scss" scoped>
</style>
